import React, { useState } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { useAuth } from '../contexts/AuthContext';
import { PlusIcon, XMarkIcon } from './Icons';
import { UserRole } from '../types';
import { database } from '../services/firebase';
import { ref, onValue } from 'firebase/database';

const MAX_FLIGHTS = 3;

const NewFlightModal: React.FC<{ onClose: () => void; }> = ({ onClose }) => {
    const { startNewFlightSession } = useFlight();
    const [flightNumbers, setFlightNumbers] = useState<string[]>([]);
    const [selectedFlight, setSelectedFlight] = useState<string>('');
    const [flightDate, setFlightDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    React.useEffect(() => {
        const flightNumbersRef = ref(database, 'config/flightNumbers');
        const unsubscribe = onValue(flightNumbersRef, (snapshot) => {
            const data = snapshot.val() || [];
            setFlightNumbers(data);
            if (data.length > 0 && !selectedFlight) {
                setSelectedFlight(data[0]);
            }
        });
        return () => unsubscribe();
    }, [selectedFlight]);

    const handleStartReport = async () => {
        if (!selectedFlight || !flightDate) {
            setError('Please select a flight number and date.');
            return;
        }
        setError('');
        setIsLoading(true);
        try {
            await startNewFlightSession(selectedFlight, flightDate);
            onClose();
        } catch (e) {
            setError('Failed to start new report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md border border-slate-700">
                <h3 className="text-xl font-semibold text-white mb-4">Start New Flight</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm text-slate-300 mb-1">Flight Number</label>
                        <select
                            value={selectedFlight}
                            onChange={(e) => setSelectedFlight(e.target.value)}
                            className="w-full"
                        >
                            <option value="" disabled>Select a flight</option>
                            {flightNumbers.map(fn => <option key={fn} value={fn}>{fn}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-slate-300 mb-1">Flight Date</label>
                        <input
                            type="date"
                            value={flightDate}
                            onChange={(e) => setFlightDate(e.target.value)}
                            className="w-full"
                        />
                    </div>
                     {error && <p className="text-red-400 text-sm text-center pt-2">{error}</p>}
                </div>
                <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-slate-700">
                    <button onClick={onClose} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                    <button onClick={handleStartReport} disabled={isLoading} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-500">
                        {isLoading ? 'Starting...' : 'Start Flight'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const FlightTabs: React.FC = () => {
    const { activeFlights, selectedFlightSlotId, selectFlight, endFlightSession } = useFlight();
    const { user } = useAuth();
    const [showNewFlightModal, setShowNewFlightModal] = useState(false);
    const [flightToEnd, setFlightToEnd] = useState<{ slotId: string; flightNo: string } | null>(null);

    const canAddFlight = user && [UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role) && Object.keys(activeFlights).length < MAX_FLIGHTS;
    const canEndFlight = user && [UserRole.ADMIN, UserRole.SUPERVISOR].includes(user.role);

    const handleEndFlightConfirm = async () => {
        if (flightToEnd) {
            await endFlightSession(flightToEnd.slotId);
            setFlightToEnd(null);
        }
    };

    return (
        <>
            <div className="bg-slate-900 border-b border-slate-700 no-print">
                <div className="container mx-auto flex items-center gap-2 px-4">
                    {Object.entries(activeFlights).map(([slotId, flightData]) => (
                        <div
                            key={slotId}
                            onClick={() => selectFlight(slotId)}
                            className={`flex items-center cursor-pointer px-4 py-2 border-b-2 font-semibold text-sm transition-colors duration-200 ${
                                selectedFlightSlotId === slotId
                                ? 'border-blue-400 text-white bg-slate-800'
                                : 'border-transparent text-slate-400 hover:bg-slate-700/50'
                            }`}
                        >
                            <span>{flightData.counterDistribution.flightNo || 'New Flight'}</span>
                            {canEndFlight && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFlightToEnd({ slotId, flightNo: flightData.counterDistribution.flightNo });
                                    }}
                                    className="ml-2 text-slate-500 hover:text-red-400 rounded-full hover:bg-red-500/10 p-0.5"
                                    title={`End Session for ${flightData.counterDistribution.flightNo}`}
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                    {canAddFlight && (
                        <button
                            onClick={() => setShowNewFlightModal(true)}
                            className="flex items-center gap-1 text-slate-400 hover:text-white hover:bg-slate-700/50 py-2 px-3 rounded-md transition-colors"
                        >
                            <PlusIcon className="w-4 h-4"/>
                            <span className="text-xs font-medium">Add Flight</span>
                        </button>
                    )}
                </div>
            </div>

            {showNewFlightModal && <NewFlightModal onClose={() => setShowNewFlightModal(false)} />}
            
            {flightToEnd && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
                    <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-center border border-slate-700">
                        <h2 className="text-xl font-bold text-white mb-4">Confirm End Session</h2>
                        <p className="text-slate-300 mb-6">
                            This will save the current data for flight <strong className="text-amber-400">{flightToEnd.flightNo}</strong> as a report and remove it from the active flights. Are you sure?
                        </p>
                        <div className="flex justify-center gap-4">
                            <button onClick={() => setFlightToEnd(null)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg">Cancel</button>
                            <button onClick={handleEndFlightConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg">Confirm & End</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FlightTabs;
